import { useNavigate, useParams } from 'react-router-dom'
import { getAtendimentoByProtocolo, isAceiteTermoPendente } from '../core/_requests'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AtendimentoView } from '../../apps/atendimento/core/_models'
import {
  AtendimentoEstado,
  getApiErrorMessage,
  showAlert,
  showError,
  toAbsoluteUrl,
} from '../../../../_metronic/helpers'
import { getTeleAuth, removeTeleAuth } from '../core/teleConsultaAuth'
import { LoadingTimer, useModal } from '../../../components'
import { NpsModal } from './modal/NpsModal'
import { EncerramentoTeleConsulta } from './EncerramentoTeleConsulta'
import { VideoAtendimentoAgora } from '../../apps/atendimento_medicos/atendimento-video/video-atendimento-agora'
import { useAtendimentoChat } from '../../apps/atendimento_medicos/atendimento-video/hooks/useAtendimentoChat'
import { ChatPanel } from '../../apps/atendimento_medicos/atendimento-video/components/ChatPanel'

export const AtendimentoTeleConsulta = () => {
  const { protocolo } = useParams()
  const { showModal } = useModal()

  const navigate = useNavigate()
  const [atendimento, setAtendimento] = useState<AtendimentoView>()
  const pacienteToken = getTeleAuth()

  const [chatOpen, setChatOpen] = useState(false)

  const { messages, unreadCount, sendMessage, aviso } = useAtendimentoChat({
    idAtendimento: atendimento?.id || 0,
    accessToken: pacienteToken?.accessToken,
    chatOpen,
    isPaciente: true,
    initialMessages: atendimento?.mensagens,
    protocolo,
  })

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const carregado = useRef(false)
  // Várias voltas do polling podem falhar juntas: um alerta só.
  const encerrando = useRef(false)

  const getAtendimento = useCallback(async () => {
    if (protocolo) {
      try {
        const atend = await getAtendimentoByProtocolo(protocolo)
        carregado.current = true
        setAtendimento(atend)
      } catch (error: any) {
        // Aceite pendente (deep link/refresh direto nesta URL): o paciente está autenticado, só
        // falta consentir — leva para a etapa 2 sem derrubar o token nem alertar a cada polling.
        if (isAceiteTermoPendente(error)) {
          navigate(`/tele-consulta/${protocolo}/consentimento`)
          return
        }
        const status = error?.response?.status
        if (status !== 401 && status !== 403) {
          // Falha passageira no polling (rede, 5xx) com a consulta já carregada: não derruba o
          // paciente da chamada; a próxima volta do polling tenta de novo.
          if (carregado.current) return
          await showError(error)
        } else if (!encerrando.current) {
          encerrando.current = true
          const mensagem = getApiErrorMessage(error)
          // 401 = token do paciente venceu; 403 com mensagem = regra do back (ex.: link não habilitado).
          showAlert(
            status === 401 || !mensagem || /^access denied$/i.test(mensagem)
              ? {
                  title: 'Sessão expirada',
                  text: 'Seu acesso à consulta expirou. Entre novamente para continuar.',
                  icon: 'warning',
                }
              : { title: 'Acesso negado', text: mensagem, icon: 'warning' }
          )
        }
        removeTeleAuth()
        navigate(`/tele-consulta/${protocolo}`)
      }
    }
  }, [navigate, protocolo])

  useEffect(() => {
    getAtendimento()

    intervalRef.current = setInterval(() => {
      getAtendimento()
    }, 5000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [getAtendimento])

  useEffect(() => {
    if (!pacienteToken?.accessToken) navigate(`/tele-consulta/${protocolo}`)
  }, [navigate, pacienteToken?.accessToken, protocolo])

  useEffect(() => {
    if (atendimento?.terminoFormatado) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      showModal({
        title: 'Avaliação',
        content: <NpsModal protocolo={String(atendimento.protocolo)} />,
      })
    }
  }, [atendimento, showModal])

  return (
    <>
      {!!atendimento?.terminoFormatado ||
      atendimento?.estado?.id === AtendimentoEstado.Cancelado ? (
        <EncerramentoTeleConsulta atendimento={atendimento} />
      ) : atendimento && atendimento.estado?.id === AtendimentoEstado.EmAtendimento ? (
        <div className='d-flex flex-column w-100 h-100 m-0 overflow-hidden'>
          <div className='d-flex flex-column fs-4 p-4'>
            <span className='me-3 '>
              Protocolo: <strong>{atendimento.protocolo}</strong>
            </span>
            <span>
              Paciente: <strong>{atendimento.paciente?.nome}</strong>
            </span>
          </div>

          <div className='d-flex flex-row flex-grow-1 w-100 h-100 m-0 p-0 overflow-hidden gap-4 px-4 pb-4'>
            <div className='flex-grow-1 position-relative' style={{ minHeight: 360 }}>
              {process.env.REACT_APP_VIDEO_APP === 'AGORA' ? (
                <VideoAtendimentoAgora
                  idAtendimento={atendimento.id!}
                  idPessoa={atendimento.paciente!.pessoa!.id}
                  isPaciente={true}
                  pacienteNome={atendimento.paciente?.nome}
                  handleNewUser={() => {}}
                  chatOpen={chatOpen}
                  setChatOpen={setChatOpen}
                  unreadCount={unreadCount}
                />
              ) : atendimento.urlSalaVideo ? (
                <iframe
                  title='Video Atendimento'
                  className='w-100 h-100 border-0 m-0 p-0'
                  src={`${toAbsoluteUrl(atendimento.urlSalaVideo)}&externalId=${
                    atendimento.paciente?.id
                  }`}
                  allow='camera; microphone; fullscreen; speaker; display-capture; autoplay'
                ></iframe>
              ) : null}
            </div>

            {chatOpen && (
              <div
                className='d-flex flex-column flex-shrink-0 bg-body shadow-sm rounded'
                style={{ width: '360px', height: '100%' }}
              >
                <ChatPanel
                  messages={messages}
                  pacienteNome={atendimento.paciente?.nome}
                  doctorNome={atendimento.responsavel?.nome}
                  isPaciente={true}
                  onSendMessage={sendMessage}
                  onClose={() => setChatOpen(false)}
                  aviso={aviso}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className='d-flex flex-column justify-content-center align-items-center flex-grow-1'>
          <div
            className='d-flex flex-column align-items-center justify-content-center bg-gray-200 rounded-4 w-100 text-center py-5'
            style={{ maxWidth: '500px', minHeight: '250px' }}
          >
            <p className='fw-bold text-primary fs-1'>Sala de Espera</p>
            {atendimento?.solicitacao && (
              <LoadingTimer className='mb-3' targetTime={atendimento?.solicitacao} />
            )}

            <p className='fs-4 mb-2'>
              Olá <strong>{atendimento?.paciente?.nome}</strong>
            </p>
            <p className='fs-6 mb-2'>
              Sua consulta está marcada para <strong>{atendimento?.solicitacaoFormatada}</strong>.
            </p>

            {atendimento?.responsavel && (
              <div className='mt-2'>
                <p className='small text-muted mb-1'>Profissional de Saúde:</p>
                <p className='fw-semibold mb-0 text-dark'>{atendimento.responsavel.nome}</p>
              </div>
            )}

            <div className='d-flex align-items-center justify-content-center text-muted small mt-2'>
              <div className='spinner-border spinner-border-sm text-primary me-2' />
              <span className='fs-6'>
                Aguarde enquanto o profissional se conecta à sua consulta
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
